import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for dynamic meta tags
 * 
 * Optimized for:
 * - Google Search
 * - AI Browsers (Perplexity, ChatGPT, etc.)
 * - AI Agents
 * - Social Media Sharing
 * 
 * Usage:
 * <SEO
 *   title="Page Title"
 *   description="Page description"
 *   keywords="keyword1, keyword2"
 *   image="/path/to/image.png"
 *   url="/page"
 *   type="website"
 * />
 */
const SEO = ({
  title = "Sniffr.ai: AI-Powered UAT Automation Engine",
  description = "Sniffr.ai is an AI-powered UAT Automation Engine that reviews code, requirements, architecture, and acceptance criteria—catching 80% of defects before QA and reducing UAT effort by 75%.",
  keywords = "UAT automation, AI UAT automation, requirement validation, AI code review, automated code review, pull request analysis, code quality, GitHub review, GitLab review, Bitbucket review, code analysis tool, software development, DevOps, AI-powered code analysis, acceptance criteria validation, SDLC validation, engineering accuracy scoring",
  image = "/assets/logo.png",
  url = "/",
  type = "website"
}) => {
  // Get base URL from environment variable or fallback to production URL
  const baseUrl = process.env.REACT_APP_BASE_URL || 
                  (typeof window !== 'undefined' ? window.location.origin : 'https://sniffr.ai');
  
  // Normalize URL: remove trailing slash and ensure it starts with /
  const normalizedPath = url.replace(/^\/+|\/+$/g, '') || '';
  const fullUrl = `${baseUrl}${normalizedPath ? '/' + normalizedPath : ''}`;
  
  // Ensure absolute URL for images
  const imageUrl = image.startsWith('http') ? image : `${baseUrl}${image}`;

  // JSON-LD structured data for Organization
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Sniffr.ai",
    "url": baseUrl,
    "logo": imageUrl,
    "description": "AI-Powered Code Review & Requirement Validation for Engineering Teams",
    "sameAs": [
      "https://github.com/sniffrai",
      "https://twitter.com/sniffrai",
      "https://x.com/Sniffr_ai",
      "https://www.linkedin.com/company/sniffr-ai",
      "https://www.facebook.com/sniffrai",
      "https://www.instagram.com/sniffr.ai/",
      "https://www.youtube.com/@Sniffrai"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Support",
      "email": "support@sniffr.ai"
    }
  };

  // JSON-LD structured data for SoftwareApplication (for homepage)
  const softwareApplicationSchema = normalizedPath === '' ? {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Sniffr.ai",
    "applicationCategory": "DeveloperApplication",
    "applicationSubCategory": "UAT Automation Engine",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": description,
    "url": fullUrl,
    "provider": {
      "@type": "Organization",
      "name": "Sniffr.ai",
      "url": baseUrl
    },
    "featureList": [
      "AI code review",
      "AI requirement validation",
      "AI UAT automation",
      "AI PR summarization",
      "Engineering accuracy scoring",
      "Enterprise dashboards",
      "Architecture review",
      "Acceptance criteria validation",
      "GitHub integration",
      "GitLab integration",
      "Bitbucket integration",
      "Azure DevOps integration"
    ]
  } : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Open Graph / Facebook / LinkedIn / AI Browsers */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content="Sniffr.ai" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      {softwareApplicationSchema && (
        <script type="application/ld+json">
          {JSON.stringify(softwareApplicationSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

